import getStoreProperty from "components/ProjectNonSpecificComponents/DevTools/Functions/getStoreProperty"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import { HEIGHT_WITH_NAV_AND_FOOTER } from "components/ProjectSpecificComponents/Constants/GLOBAL_CONSTANTS"
import ROUTES from "components/ProjectSpecificComponents/Constants/ROUTES"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import {
	TOnClickTableRow,
	TTableHeadCell,
} from "components/ProjectSpecificComponents/Interfaces/TableInterface"
import { observer } from "mobx-react"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router"
import { usePTKMStore } from "../Contexts/PTKMStoreContext"
import IconButton from "../Elements/Global/atoms/Buttons/IconButton"
import PreferencesSmallButton from "../Elements/Global/atoms/Buttons/PreferencesSmallButton"
import NewConfigurationModal from "../Elements/Global/molecules/Modals/NewConfigurationModal"
import { Td, Tr } from "../Elements/Tables/organisms/Table/StyledComponents/TableStyleComp"
import Table from "../Elements/Tables/organisms/Table/Table"
import { IJamaItem, IJamaProject, IJamaProjectsAndFolders } from "../Interfaces/DataInterface"
import { defaultFormData } from "../Stores/PTKMStore/defaults"
import defaultPtkmConfig from "../Stores/PTKMStore/defaults"
import { IptkmFormConfig, TptkmConfig } from "../Stores/PTKMStore/ptkmStore"
import ConfigSectionButtons from "../Elements/Navigation/atoms/ConfigSectionButtons"
import insertInArr from "../Functions/insertInArr"
import parseDataForReactSelect from "../Functions/parseDataForReactSelect"
import { lostOrFinishedAdminSession } from "../API/Functions/lostOrFinishedAdminSession"

interface Props {}

const PTKMConfigurationOverviewTable = observer(({}: Props) => {
	const ptkmStore = usePTKMStore()
	const store = useStore()
	const [isNewConfigModalShown, setIsNewConfigModalShown] = useState(false)
	const [selectedConfigId, setSelectedConfigId] = useState<number>(0)
	const [modalMode, setModalMode] = useState<"new" | "edit">("new")
	const { t } = useTranslation()
	const history = useHistory()
	const { isMobile } = useScreenType()
	const { formsConfigsPTKM } = ptkmStore.settings

	const head: TTableHeadCell[] = [
		{
			key: "position",
			label: t("forms.field-label.number"),
			searchable: true,
			sortable: true,
		},
		{
			key: "isActive",
			label: t("forms.field-label.status"),
			searchable: true,
			sortable: true,
		},
		{
			key: "name",
			label: t("forms.field-label.name"),
			searchable: true,
			sortable: true,
		},
		{
			key: "project",
			label: t("forms.field-label.project"),
			searchable: true,
			sortable: true,
		},
		{
			key: "item",
			label: t("forms.field-label.item"),
			searchable: true,
			sortable: true,
		},
		{
			key: "actions",
			label: t("forms.field-label.actions"),
			searchable: false,
			sortable: false,
			disabled: true,
		},
	]

	useEffect(() => {
		const getAllItems = async () => {
			store.setLoaderOverlayOpen(true)
			const { ok, data } = await AppRequests("ItemType").get<IJamaItem[]>("all")
			store.setLoaderOverlayOpen(false)
			if (ok && data) {
				ptkmStore.setAllItems(parseDataForReactSelect(data))
			}
		}
		fetchPTKMSettings(true)
		getAllItems()
	}, [])

	const fetchPTKMSettings = async (latest: boolean = false) => {
		const getLatestData: boolean = latest
		store.setLoaderOverlayOpen(true)
		const { ok, statusCode } = await ptkmStore.fetchSettings(getLatestData)
		store.setLoaderOverlayOpen(false)
		if (statusCode == 404) {
			saveDefaultData()
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else if (!ok) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "settings.failedTotal",
				descriptionKey: "settings.totalFail",
			})
		}
	}

	const saveDefaultData = async () => {
		const { ok } = await AppRequests("PTKM").post<TptkmConfig>("save-settings", defaultPtkmConfig)

		if (!ok) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "settings.failed",
				descriptionKey: "settings.failedDescription",
			})
		}
	}

	const deleteFormsConfiguration = async (id: string, position: number) => {
		if (formsConfigsPTKM[position].isActive) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "config.delete-failed",
				descriptionKey: "config.delete-failed-description",
			})
			return
		}
		deleteConfig(id, "config.deleted")
	}

	const postSettingsNewConfig = async (formData: Partial<IptkmFormConfig>) => {
		const useCopiedConfigAsDefaultData: boolean = !!ptkmStore.copiedConfig

		const { name, position } = formData
		const baseConfig = useCopiedConfigAsDefaultData
			? {
					...ptkmStore.copiedConfig,
					name,
					position,
			  }
			: formData

		const { ok, data, statusCode } = await AppRequests("PTKM").post<
			Partial<IptkmFormConfig>,
			TptkmConfig
		>("saveNewPTKMConfig", baseConfig)
		if (ok && data) {
			fetchPTKMSettings(true)
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "settings.failed",
				descriptionKey: "settings.failedDescription",
			})
		}
	}

	const updateConfig = async (formData?: Partial<IptkmFormConfig>, alertTitle?: string) => {
		if (!formData) return

		//Have to clean state to uptade table
		const { ok, data, statusCode } = await AppRequests("PTKM").put<
			Partial<IptkmFormConfig>,
			TptkmConfig
		>("updatePTKMConfig", formData, {
			formsConfigId: formData.id,
		})
		if (ok && data) {
			fetchPTKMSettings(true)
			store.setInfoModalConfig({
				type: "info",
				titleKey: alertTitle ?? "data-saving.success",
				showDuration: 1000,
			})
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "data-saving.failed",
			})
		}
	}

	const deleteConfig = async (id: string, alertTitle?: string) => {
		store.setLoaderOverlayOpen(true)
		//Have to clean state to uptade table
		const { ok, data, statusCode } = await AppRequests("PTKM").delete<TptkmConfig>(
			"deletePTKMConfig",
			{
				requestedItemId: id,
			}
		)

		if (ok && data) {
			//formStore.mergeSettingsDataSafely(data)
			fetchPTKMSettings(true)
			store.setInfoModalConfig({
				type: "info",
				titleKey: alertTitle ?? "data-saving.success",
				showDuration: 1000,
			})
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "data-saving.failed",
			})
		}
		store.setLoaderOverlayOpen(false)
	}

	useEffect(() => {
		const isConnected: string | null = sessionStorage.getItem("checkConnection")
		if (isConnected === "true") store.setHasConnectionBeenMade(true)
	}, [])

	const onClickTableRow: TOnClickTableRow = async (rowId, row) => {
		if (!store.hasConnectionBeenMade) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "",
				descriptionKey: "rawText.connect-before-edit",
			})
			return
		}

		/**
		 * Find selected configuration and open it
		 */
		formsConfigsPTKM.forEach(({ id }) => {
			if (id === row.id) {
				ptkmStore.setConfig(row)
				history.push(ROUTES.PTKM_CONFIGURATION)
			}
		})
	}

	const tableRow = (row: IptkmFormConfig) => {
		const position = row["position"]
		const isActive = row["isActive"]
		const name = row["name"]
		const id = row["id"]
		const project = row["project"]
		const item = row["item"]

		return (
			<>
				<Td>{position + 1}</Td>
				<Td>
					<HyperDiv>
						<HyperDiv
							width="fit-content"
							padding="0.3rem"
							style={{
								backgroundColor: isActive ? "var(--active)" : "var(--gray-ec)",
								border: isActive ? " 1px solid var(--active)" : " 1px solid var(--gray-ec)",
								borderRadius: "5px",
							}}
						>
							{isActive ? t("rawText.active") : t("rawText.inactive")}
						</HyperDiv>
					</HyperDiv>
				</Td>
				<Td>{name}</Td>

				<Td>{JSON.parse(project).label}</Td>
				<Td>{JSON.parse(item).label}</Td>
				{/* <Td>{id}</Td> */}
				<Td>
					<HyperDiv direction="row" centered alignedRight>
						<IconButton
							style={{ width: "31px", height: "31px" }}
							iconWidth="15px"
							iconHeight="15px"
							iconColor="var(--red-b2)"
							iconKey="cross"
							backgroundColorOnHover="var(--red-b2)"
							iconColorOnHover="var(--white)"
							onClick={(e) => {
								deleteFormsConfiguration(id, position)
								e.stopPropagation()
							}}
						/>

						<Whitespace h x="0.1" />
						<IconButton
							style={{ width: "31px", height: "31px" }}
							iconWidth="15px"
							iconHeight="15px"
							iconKey="pencil"
							backgroundColorOnHover="var(--blue)"
							iconColorOnHover="var(--white)"
							onClick={(e) => {
								setIsNewConfigModalShown(true)
								setModalMode("edit")
								setSelectedConfigId(position)
								e.stopPropagation()
							}}
						/>
						<Whitespace h x="2" />
					</HyperDiv>
				</Td>
			</>
		)
	}

	const newForm: IptkmFormConfig = {
		...defaultFormData,
		position: formsConfigsPTKM.length ?? 0,
	}

	return (
		<HyperDiv height={HEIGHT_WITH_NAV_AND_FOOTER} style={{ overflowY: "auto" }}>
			<ConfigSectionButtons />

			<HyperDiv padding={isMobile ? "" : "0 5rem"}>
				<Table
					search
					head={head}
					tableName={t("rawText.ptkm-configuration-list")}
					records={formsConfigsPTKM}
					tableRow={tableRow}
					clickedOnRow={onClickTableRow}
					fixedColumnWidth
					HeaderExtraElement={
						<PreferencesSmallButton
							onClick={(e) => {
								setModalMode("new")
								setIsNewConfigModalShown(true)
								e.stopPropagation()
							}}
							children={t("button.add")}
						/>
					}
					scrollRecords={{ set: true, bodyHeight: "calc(100vh - 310px)" }}
				/>

				{isNewConfigModalShown && (
					<NewConfigurationModal
						onClose={() => setIsNewConfigModalShown(false)}
						type="PTKM"
						configData={modalMode == "new" ? newForm : formsConfigsPTKM[selectedConfigId]}
						onSave={modalMode == "new" ? postSettingsNewConfig : updateConfig}
						modalTitle={modalMode == "new" ? t("rawText.new-config") : t("rawText.edit-config")}
						modalButtonName={modalMode == "new" ? t("button.add") : t("rawText.save")}
						newConfig={modalMode == "new"}
					/>
				)}
			</HyperDiv>
		</HyperDiv>
	)
})

export default PTKMConfigurationOverviewTable
